import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const links = [
    { to: "/work", label: "Work" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
                scrolled ? "bg-black/60 backdrop-blur-xl" : "bg-transparent"
            }`}
        >
            <div className="flex items-center justify-between px-6 md:px-12 h-20">
                <Link to="/" data-testid="nav-logo" className="flex items-center group" aria-label="MIDI Pictures">
                    <img
                        src="/midi-logo.png"
                        alt="MIDI Pictures"
                        className="h-10 md:h-12 w-auto select-none"
                        style={{
                            mixBlendMode: "lighten",
                            filter: "grayscale(100%) brightness(8)",
                        }}
                        draggable={false}
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="midi-link text-[13px] tracking-[0.18em] uppercase font-medium text-white/85 hover:text-white"
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <button
                    aria-label="Menú"
                    data-testid="nav-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden h-10 w-10 flex flex-col items-end justify-center gap-[6px]"
                >
                    <span className={`block h-px bg-white transition-all duration-500 ${open ? "w-6 rotate-45 translate-y-[3px]" : "w-6"}`} />
                    <span className={`block h-px bg-white transition-all duration-500 ${open ? "w-6 -rotate-45 -translate-y-[4px]" : "w-4"}`} />
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key="mobile-nav"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden bg-black/95 backdrop-blur-2xl border-t border-white/5"
                    >
                        <div className="flex flex-col px-6 py-8 gap-6">
                            {links.map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-link-mobile-${l.label.toLowerCase()}`}
                                    className="font-display text-3xl tracking-tightest text-white"
                                >
                                    {l.label}
                                </Link>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
